import { useCallback, useEffect, useState } from "react";
import { apiRequest } from "../../../shared/api/client";
import type { ProductionReferenceData } from "../../production-execution/models";
import type { Warehouse, WarehouseUser } from "./settings-types";
import type { Dispatch, SetStateAction } from "react";
type Setter<T> = Dispatch<SetStateAction<T>>;
export function usePreparationSettingsCatalog({ dataRefreshVersion, setError, setSelectedUserId, setLineId, setProductCode }: {
    dataRefreshVersion: number;
    setError: Setter<string | null>;
    setSelectedUserId: Setter<string>;
    setLineId: Setter<string>;
    setProductCode: Setter<string>;
}) {
    const [reference, setReference] = useState<ProductionReferenceData | null>(null);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [warehouseUsers, setWarehouseUsers] = useState<WarehouseUser[]>([]);
    const [loading, setLoading] = useState(true);
    const loadReference = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [production, warehouseResult, team] = await Promise.all([
                apiRequest<ProductionReferenceData>("/api/production/reference-data", {
                    cache: "no-store",
                }),
                apiRequest<{
                    items: Warehouse[];
                }>("/api/material-preparation/warehouses", { cache: "no-store" }),
                apiRequest<WarehouseUser[]>("/api/team", { cache: "no-store" }),
            ]);
            setReference(production);
            setWarehouses(warehouseResult.items);
            const eligibleUsers = team.filter((user) => user.status === "Aktif" &&
                user.roles.some((role) => ["DEPO_GOREVLISI", "CEP_DEPO_SORUMLUSU"].includes(role)));
            setWarehouseUsers(eligibleUsers);
            setSelectedUserId((current) => eligibleUsers.some((user) => user.id === current)
                ? current
                : (eligibleUsers[0]?.id ?? ""));
            const initialLine = production.lines[0];
            const plannedProduct = production.scheduleItems.find((item) => item.lineId === initialLine?.id && item.productCode)?.productCode;
            const mikroProduct = production.workOrders.find((order) => order.plannedWorkCenterCode === initialLine?.mikroWorkCenterCode ||
                order.plannedWorkCenterName === initialLine?.name)?.productCode;
            setLineId((current) => current || initialLine?.id || "");
            setProductCode((current) => current || plannedProduct || mikroProduct || "");
        }
        catch (loadError) {
            setError(loadError instanceof Error
                ? loadError.message
                : "Hat ve Mikro depo bilgileri alınamadı.");
        }
        finally {
            setLoading(false);
        }
    }, [setError, setSelectedUserId, setLineId, setProductCode]);
    useEffect(() => {
        void loadReference();
    }, [dataRefreshVersion, loadReference]);
    return { reference, warehouses, warehouseUsers, loading };
}
